import { watch } from 'vue';
import { AnyFormSettingsKey } from '../form-settings/form-experience/form-settings-keys.js';
import { initFormState, useFormState } from "./use-form-state.js";
import { initMarketoFormEvents, useFormEvents } from './use-form-events.js';
import { initMarketoForm, useMarketoForm, useMarketoFormInternals } from "./use-marketo-form-instance.js";
import { initEclipseHtmlFormElement } from "./use-eclipse-html-form-element.js";
import { initMarketoRecaptcha } from "./use-marketo-recaptcha.js";
import { initHiddenFieldInitializer, useHiddenFieldInitializer } from "./use-hidden-field-initializer.js";
import type { FormWidgetData } from "../../widgetDataTs.js";
import { initGatingLevel, useGatingLevel } from './use-gating-level.js';
import type { MarketoFormState } from '../types/definitions/marketo-form-state.js';
import { HiddenMarketoInputName } from '../types/definitions/inputs/marketo-input-names-and-values.js';
import { initMarketoHiddenInputs } from './use-marketo-hidden-inputs.js';
import { initInputsMetaData } from "./use-inputs-meta-data.js";
import { initFormSettings, useFormSettings } from "./use-form-settings.js";
import { initMarketoKnownVisitorState } from './use-marketo-known-visitor-status.js';
import { initBlindSubmit } from './use-blind-submit.js';
import { initInputState, useInputState } from './use-input-state.js';
import { initEnabledFields } from './use-enabled-fields.js';
import { type MarketoFormSettingsKey } from '../form-settings/marketo-form/marketo-form-setting.js';
import { initFormSubmit } from './use-form-submit.js';
import { initFormUserInputValues, useFormUserInputValues } from "./use-form-user-input-values.js";
import { initPrivacyConsentState } from './use-privacy-consent-state.js';
import { initFormAssetDownloads } from './use-form-asset-download.js';
import { initFormTracking, useFormTracking } from './use-form-tracking.js';
import { initFormAbandonmentManager } from './use-form-abandonment.js';
import type { SiteCountryCode } from '../../types-and-definitions/locale-defs/Locales.js';
import { initSiteLanguage } from "./use-site-language.js";
import { initFormLogger, useFormLogger } from './use-form-logger.js';
import { isNil } from '@mcwd/typescript-type-guards';
import type { GatingLevel } from '../types/definitions/gating/gating-level.js';
import { initUseTranslation } from './use-translation.js';
import { initValidations } from './use-input-validation.js';
import { initAbandonmentListeners, useAbandonmentListeners } from './use-abandonment-trigger.js';

export { useFormSettings } from "./use-form-settings.js";
export { useFormState } from "./use-form-state.js";
export { useMarketoForm, useMarketoFormInternals } from "./use-marketo-form-instance.js";
export { useEclipseHtmlFormElement } from "./use-eclipse-html-form-element.js";
export { useMarketoRecaptcha } from "./use-marketo-recaptcha.js";
export { useGatingLevel } from "./use-gating-level.js";
export { useInputsMetaData } from "./use-inputs-meta-data.js";
export { useInputState, type InputStateOption } from "./use-input-state.js";
export { useSubmitState } from "./use-submit-state.js";
export { useFormSubmit } from "./use-form-submit.js";
export { useBlindSubmit } from "./use-blind-submit.js";
export { useSiteLanguage } from "./use-site-language.js";
export { useTranslation } from "./use-translation.js";

export { type InputState } from "./use-input-state.js";
export { type SubmitState, type SubmitStateKey } from "./use-submit-state.js";


export interface InitFormServicesOptions {
  formSettingsKey: AnyFormSettingsKey;
  widgetData: FormWidgetData,
  isInModal: boolean,
  lang: SiteCountryCode
  customData?: Record<string, any> | undefined;
  gatingLevelCustom?: GatingLevel.FormExperience
}


export function initFormServices({ formSettingsKey, widgetData, isInModal, lang, customData, gatingLevelCustom }: InitFormServicesOptions) {
  initFormLogger(formSettingsKey);
  const { logger } = useFormLogger({ callerLabel: "initFormServices"});
  logger.time("initFormServices");
  initSiteLanguage(lang);
  initValidations();
  initFormSettings(formSettingsKey);
  initUseTranslation();
  initMarketoKnownVisitorState();
  initMarketoFormEvents();
  initEclipseHtmlFormElement();
  const { FormSettings } = useFormSettings({ callerLabel: "initFormServices" });
  initGatingLevel({ widgetData, formSettingsKey, gatingLevelCustom });
  initFormState();
  initEnabledFields();
  initFormUserInputValues();
  initPrivacyConsentState();
  initInputState();
  initMarketoForm();
  initMarketoRecaptcha();
  initFormSubmit();
  initBlindSubmit();
  initFormAssetDownloads();
  initAbandonmentListeners();

  initHiddenFieldInitializer({
    formSettingsKey,
    widgetData,
    leadSource: "Website",
    customDocumentDownloadURL: customData?.customDocumentDownloadURL as string | undefined,
  });
  initFormTracking({ formTrackingName: FormSettings.formTrackingName, widgetData, isInModal });

  initInputsMetaData();

  const { FormState } = useFormState({ callerLabel: "initFormServices" });
  const events = useFormEvents({ callerLabel: "initFormServices" });

  watch(
    () => FormState.value,
    (newState: MarketoFormState, prevState: MarketoFormState) => {
      logger.debug(`Form State Changed: { newState: "${newState}", prevState: "${prevState}" }`);
    }
  );

  const { LoadForm } = useMarketoForm({ callerLabel: "initFormServices" });

  LoadForm().catch((err) => logger.error(err));

  const { setHiddenFields, hiddenFieldValueBuilder } = useHiddenFieldInitializer({ callerLabel: "initFormServices" });
  const { setCookieGatingLevelState, FormGatingLevel } = useGatingLevel({ callerLabel: "initFormServices" });

  initMarketoHiddenInputs(HiddenMarketoInputName);

  (Object.keys(events) as (keyof typeof events)[])
    .filter((key) => key !== "marketoValidation")
    .forEach((key) => {
      const eventHook = events[key];
      if (!("on" in eventHook)) {
        return;
      }
      eventHook.on(({ eventName }: { eventName: string }) => {
        logger.debug(`FormEvent: ${eventName}`);
      });
    });

  events.marketoValidation.on(({ eventName, marketoIsValid }) => {
    logger.debug(`FormEvent: ${eventName}(mktoIsValid="${marketoIsValid.toString()}`);
  });

  const { setInputStates, inputStates } = useInputState();
  const { setFormUserInputValuesFromSettings, userInputValues } = useFormUserInputValues();

  useFormTracking({ callerLabel: "use-form-services" });

  events.marketoSetup.on(() => {
    setHiddenFields();
  });

  watch(() => FormState.value, (val) => {
    if (val === "BlindForm") {
      logger.log("Reset values for blind form");
      setFormUserInputValuesFromSettings();
      setInputStates(userInputValues, {});
      setHiddenFields();
    }
  });

  const { MarketoFormHtmlElement } = useMarketoFormInternals({ callerLabel: "initFormServices" });

  watch(() => hiddenFieldValueBuilder.hiddenFieldValuesObject.value, () => {
    if (isNil(MarketoFormHtmlElement.value) === false) {
      setHiddenFields();
    }
  });

  events.completed.on(() => {
    if (FormSettings.gatingCookiePermissions.allowWrite) {
      setCookieGatingLevelState(FormGatingLevel);
    }
  });

  // All form abandonment logic
  initFormAbandonmentManager({ formName: FormSettings.formTrackingName, isInModal, inputStates, widgetData });
  
  useAbandonmentListeners({ callerLabel: "initFormServices" });
  logger.timeEnd("initFormServices");
}

export function useFormServices<
  TFormOrFormExperience extends "MarketoForm" | "FormExperience" = "FormExperience",
  const TFormSettingsKey extends MarketoFormSettingsKey | AnyFormSettingsKey =
    TFormOrFormExperience extends "MarketoForm" 
          ? MarketoFormSettingsKey 
          : AnyFormSettingsKey
> ({ callerLabel }: { callerLabel?: string | undefined } = {})
{
  const { logger } = useFormLogger({ callerLabel: "useFormServices" });
  try {
    callerLabel ??= "useFormServices";
    const { FormSettings, FormClasses } = useFormSettings<TFormOrFormExperience, TFormSettingsKey>({ callerLabel });
    const { FormState } = useFormState({ callerLabel });
    const { 
      CurrentMarketoFormGatingState,
      CurrentFormExperienceGatingState,
      FormGatingLevel,
      FormExperienceGatingLevel
    } = useGatingLevel({ callerLabel });
    const { UniqInstanceId } = useMarketoForm({ callerLabel });

    return {
      FormSettings,
      FormClasses,
      FormState,
      FormGatingLevel,
      FormExperienceGatingLevel,
      CurrentMarketoFormGatingState,
      CurrentFormExperienceGatingState,
      UniqInstanceId
    };
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
}